import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Trash2, X, Key, Save } from 'lucide-react';
import { vendorsApi, type Vendor, type TenantSearchResult, type UpdateVendorData } from '../../api/endpoints/vendors';
import { queryClient } from '../../providers/QueryProvider';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { PageSpinner } from '../../components/ui/Spinner';
import { formatDate, formatTimeAgo } from '../../utils/formatters';

export function VendorDetailAdminPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const vendorId = id;

  const { data, isLoading } = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => vendorsApi.get(vendorId!),
    enabled: !!vendorId,
  });

  const deleteMutation = useMutation({
    mutationFn: () => vendorsApi.delete(vendorId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      navigate('/vendors');
    },
  });

  if (isLoading) return <PageSpinner />;

  const vendor = data?.vendor;
  const deviceStats = data?.device_stats || { total: 0, connected: 0 };

  if (!vendor) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Vendor not found</p>
        <Link to="/vendors" className="text-purple-600 text-sm mt-2 inline-block">Back to vendors</Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/vendors')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <ArrowLeft size={20} />
          </button>
          <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <span className="text-purple-600 dark:text-purple-400 font-bold text-sm">{vendor.name.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">{vendor.name}</h1>
              <Badge
                variant={vendor.status === 'active' ? 'green' : vendor.status === 'suspended' ? 'red' : 'yellow'}
                dot
              >
                {vendor.status}
              </Badge>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{vendor.vendor_id}</p>
          </div>
        </div>
        <button
          onClick={() => { if (confirm('Delete this vendor? This cannot be undone.')) deleteMutation.mutate(); }}
          className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 flex items-center gap-1"
        >
          <Trash2 size={14} />
          Delete
        </button>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column: Edit Form + Settings + Password */}
        <div className="lg:col-span-2 space-y-4">
          <EditVendorForm vendor={vendor} vendorId={vendorId!} />
          <SessionSettingsForm vendor={vendor} vendorId={vendorId!} />
          <ResetPasswordForm vendorId={vendorId!} />
        </div>

        {/* Right column: Quick Info + Sessions */}
        <div className="space-y-4">
          <Card title="Quick Info">
            <div className="p-4 space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Vendor ID</span>
                <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded font-mono">{vendor.vendor_id}</code>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Username</span>
                <span className="font-medium text-gray-800 dark:text-gray-200">{vendor.username}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Devices</span>
                <span className="font-medium text-gray-800 dark:text-gray-200">{deviceStats.total} ({deviceStats.connected} online)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Last Login</span>
                <span className="text-gray-800 dark:text-gray-200">{vendor.last_login ? formatTimeAgo(vendor.last_login) : 'Never'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Created</span>
                <span className="text-gray-800 dark:text-gray-200">{formatDate(vendor.createdAt)}</span>
              </div>
            </div>
          </Card>

          <ActiveSessionsCard vendor={vendor} vendorId={vendorId!} />
        </div>
      </div>
    </div>
  );
}

// --- Edit Vendor Form ---

function EditVendorForm({ vendor, vendorId }: { vendor: Vendor; vendorId: string }) {
  const [name, setName] = useState(vendor.name);
  const [email, setEmail] = useState(vendor.email || '');
  const [phone, setPhone] = useState(vendor.phone || '');
  const [status, setStatus] = useState(vendor.status);
  const [tenantId, setTenantId] = useState(vendor.tenant_id);
  const [tenantName, setTenantName] = useState(vendor.tenant_name || '');
  const [subdomain, setSubdomain] = useState(vendor.subdomain || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Tenant search
  const [tenantQuery, setTenantQuery] = useState(vendor.subdomain || vendor.tenant_name || '');
  const [tenantResults, setTenantResults] = useState<TenantSearchResult[]>([]);
  const [tenantSearching, setTenantSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showSelected, setShowSelected] = useState(true);

  const updateMutation = useMutation({
    mutationFn: (data: UpdateVendorData) => vendorsApi.update(vendorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      setSuccess('Vendor updated');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err: Error) => setError(err.message),
  });

  const handleSearchTenants = async () => {
    if (tenantQuery.length < 2) return;
    setTenantSearching(true);
    try {
      const data = await vendorsApi.searchTenants(tenantQuery);
      setTenantResults(data.tenants || []);
      setShowResults(true);
    } catch {
      setTenantResults([]);
    } finally {
      setTenantSearching(false);
    }
  };

  const handleSelectTenant = (t: TenantSearchResult) => {
    const tid = t._id || t.id || t.tenant_id || '';
    const tname = t.name || t.tenant_name || '';
    const tsub = t.subdomain || '';
    setTenantId(tid);
    setTenantName(tname);
    setSubdomain(tsub);
    setTenantQuery(tsub || tname);
    setShowResults(false);
    setShowSelected(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    updateMutation.mutate({
      name, email, phone, status,
      tenant_id: tenantId,
      tenant_name: tenantName,
      subdomain: subdomain,
    });
  };

  const inputClass = 'w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs focus:ring-1 focus:ring-purple-500 dark:bg-gray-700 dark:text-white';
  const labelClass = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <Card title="Vendor Details">
      <form onSubmit={handleSubmit} className="p-4 space-y-3">
        {error && <div className="p-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-400">{error}</div>}
        {success && <div className="p-2 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-xs text-green-700 dark:text-green-400">{success}</div>}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value as typeof status)} className={inputClass}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            <input type="text" value={phone} onChange={e => setPhone(e.target.value)} className={inputClass} />
          </div>
        </div>

        {/* Tenant Search */}
        <div>
          <label className={labelClass}>Tenant (Search by subdomain)</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={tenantQuery}
              onChange={e => setTenantQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSearchTenants(); } }}
              placeholder="Search subdomain..."
              className={`flex-1 ${inputClass}`}
            />
            <button
              type="button"
              onClick={handleSearchTenants}
              disabled={tenantSearching || tenantQuery.length < 2}
              className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 text-xs font-medium whitespace-nowrap disabled:opacity-50"
            >
              {tenantSearching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {showResults && tenantResults.length > 0 && (
            <div className="mt-1 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 shadow-sm z-10 relative">
              {tenantResults.map((t, i) => {
                const tid = t._id || t.id || t.tenant_id || '';
                const tname = t.name || t.tenant_name || '';
                const tsub = t.subdomain || '';
                return (
                  <div
                    key={i}
                    className="px-3 py-2 hover:bg-purple-50 dark:hover:bg-purple-900/20 cursor-pointer flex items-center justify-between text-xs border-b border-gray-100 dark:border-gray-600"
                    onClick={() => handleSelectTenant(t)}
                  >
                    <div>
                      <span className="font-semibold text-gray-800 dark:text-white">{tsub}</span>
                      {tname && <span className="text-gray-500 dark:text-gray-400 ml-1">({tname})</span>}
                    </div>
                    <span className="text-gray-400 font-mono text-[10px]">{tid.substring(0, 24)}</span>
                  </div>
                );
              })}
            </div>
          )}

          {showSelected && tenantId && (
            <div className="mt-2 p-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold text-purple-800 dark:text-purple-300">{tenantName || tenantId}</span>
                  {subdomain && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({subdomain})</span>}
                </div>
                <button
                  type="button"
                  onClick={() => { setShowSelected(false); setTenantQuery(''); }}
                  className="text-red-500 hover:text-red-700 text-xs flex items-center gap-0.5"
                >
                  <X size={12} /> Change
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Tenant ID: {tenantId}</p>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={updateMutation.isPending}
          className="bg-purple-600 text-white px-4 py-1.5 rounded-md text-xs font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
        >
          <Save size={14} />
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </Card>
  );
}

// --- Session Settings Form ---

function SessionSettingsForm({ vendor, vendorId }: { vendor: Vendor; vendorId: string }) {
  const [maxScreens, setMaxScreens] = useState(vendor.settings?.max_screens || 1);
  const [sessionTtl, setSessionTtl] = useState(vendor.settings?.session_ttl_days || 1);
  const [portalEnabled, setPortalEnabled] = useState(vendor.settings?.portal_enabled !== false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const updateMutation = useMutation({
    mutationFn: () => vendorsApi.update(vendorId, {
      settings: {
        max_screens: maxScreens,
        session_ttl_days: sessionTtl,
        portal_enabled: portalEnabled,
      },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
      setSuccess('Settings updated');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err: Error) => setError(err.message),
  });

  const inputClass = 'w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs focus:ring-1 focus:ring-purple-500 dark:bg-gray-700 dark:text-white';
  const labelClass = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <Card title="Session Settings">
      <form onSubmit={e => { e.preventDefault(); updateMutation.mutate(); }} className="p-4 space-y-3">
        {error && <div className="p-2 rounded-md bg-red-50 dark:bg-red-900/20 text-xs text-red-700 dark:text-red-400">{error}</div>}
        {success && <div className="p-2 rounded-md bg-green-50 dark:bg-green-900/20 text-xs text-green-700 dark:text-green-400">{success}</div>}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Max Screens</label>
            <input type="number" value={maxScreens} onChange={e => setMaxScreens(Number(e.target.value))} min={1} max={50} className={inputClass} />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Concurrent login sessions</p>
          </div>
          <div>
            <label className={labelClass}>Session TTL (days)</label>
            <input type="number" value={sessionTtl} onChange={e => setSessionTtl(Number(e.target.value))} min={1} max={365} className={inputClass} />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">How long each session lasts</p>
          </div>
          <div>
            <label className={labelClass}>Portal Access</label>
            <select value={String(portalEnabled)} onChange={e => setPortalEnabled(e.target.value === 'true')} className={inputClass}>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={updateMutation.isPending}
          className="bg-purple-600 text-white px-4 py-1.5 rounded-md text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
        >
          {updateMutation.isPending ? 'Updating...' : 'Update Settings'}
        </button>
      </form>
    </Card>
  );
}

// --- Reset Password Form ---

function ResetPasswordForm({ vendorId }: { vendorId: string }) {
  const [password, setPassword] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const resetMutation = useMutation({
    mutationFn: () => vendorsApi.resetPassword(vendorId, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
      setPassword('');
      setSuccess('Password reset successfully');
      setError('');
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (err: Error) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirm('Reset vendor password? This will log them out of all sessions.')) return;
    setError('');
    resetMutation.mutate();
  };

  return (
    <Card title="Reset Password">
      <form onSubmit={handleSubmit} className="p-4">
        {error && <div className="mb-3 p-2 rounded-md bg-red-50 dark:bg-red-900/20 text-xs text-red-700 dark:text-red-400">{error}</div>}
        {success && <div className="mb-3 p-2 rounded-md bg-green-50 dark:bg-green-900/20 text-xs text-green-700 dark:text-green-400">{success}</div>}
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">New Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              minLength={6}
              required
              className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs focus:ring-1 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <button
            type="submit"
            disabled={resetMutation.isPending || password.length < 6}
            className="bg-red-600 text-white px-4 py-1.5 rounded-md text-xs font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Key size={14} />
            {resetMutation.isPending ? 'Resetting...' : 'Reset Password'}
          </button>
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">This will log out the vendor from all active sessions</p>
      </form>
    </Card>
  );
}

// --- Active Sessions Card ---

function ActiveSessionsCard({ vendor, vendorId }: { vendor: Vendor; vendorId: string }) {
  const sessions = vendor.active_sessions || [];

  const disconnectMutation = useMutation({
    mutationFn: (sessionId: string) => vendorsApi.deleteSession(vendorId, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] });
    },
  });

  return (
    <Card
      title="Active Sessions"
      action={<span className="text-xs text-gray-400">{sessions.length} / {vendor.settings?.max_screens || 1}</span>}
    >
      <div className="p-4">
        {sessions.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-2">No active sessions</p>
        ) : (
          <div className="space-y-2">
            {sessions.map(s => (
              <div key={s.session_id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{s.ip || 'Unknown IP'}</span>
                  <button
                    onClick={() => {
                      if (confirm('Force disconnect this session?')) {
                        disconnectMutation.mutate(s.session_id);
                      }
                    }}
                    className="text-red-600 hover:text-red-800 dark:text-red-400 text-xs font-medium"
                  >
                    Disconnect
                  </button>
                </div>
                <p className="text-xs text-gray-400 truncate" title={s.user_agent || ''}>
                  {(s.user_agent || 'Unknown').substring(0, 40)}...
                </p>
                <p className="text-xs text-gray-400">Since {s.logged_in_at ? formatDate(s.logged_in_at) : '--'}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
